import { contextStore } from "../auth/context-store.js";
import { sessionRequestContext } from "../auth/request-context.js";
import { PermissionCode } from "../../permissions/permissions.js";
import type { AIReviewSubjectType } from "../../domain/ai-review/ai-review-model.js";

const esc = (v: unknown): string => String(v ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
const errorText = (e: unknown, fallback: string): string => (e as { safeMessage?: string })?.safeMessage || (e as Error)?.message || fallback;

interface Message {
  readonly role: "user" | "assistant";
  readonly text: string;
  readonly refs?: ReadonlyArray<{ document: string; revision: string; section: string }>;
  readonly suggestion?: { fieldPath: string; label: string; text: string; baseText: string } | null;
  readonly error?: boolean;
}

const EXAMPLES = [
  "Qual informação falta?",
  "Revise minha conclusão.",
  "Essa recomendação está coerente?",
  "Qual mecanismo de dano pode estar associado a esta condição?",
  "Explique API 570 neste contexto."
];

/**
 * <ai-assistant-drawer data-type="ME" data-id="…">
 *
 * ASSISTENTE TÉCNICO INSPECT IA — painel lateral. Dentro de um relatório, o
 * contexto (Cliente, Projeto, TAG, resultados, normas, pendências) vai
 * sozinho: a pessoa não repete nada. As respostas citam a Base Técnica; sem
 * fonte, o assistente diz que não encontrou suporte. Nunca escreve no
 * relatório: "APLICAR AO CAMPO" só com confirmação.
 */
export class AiAssistantDrawer extends HTMLElement {
  private opened = false;
  private heading = "Assistente Técnico";
  private messages: Message[] = [];
  private busy = false;

  connectedCallback(): void {
    this.addEventListener("click", e => this.onClick(e));
    this.addEventListener("keydown", e => {
      const ke = e as KeyboardEvent;
      if (ke.key === "Enter" && !ke.shiftKey && (ke.target as HTMLElement).id === "aia-input") { ke.preventDefault(); void this.send(); }
      if (ke.key === "Escape") this.close();
    });
    this.render();
  }

  /** Abre o painel; `type`/`id` definem o documento de contexto (ou nenhum). */
  open(opts: { title?: string; type?: AIReviewSubjectType; id?: string } = {}): void {
    if (opts.type) this.dataset["type"] = opts.type;
    if (opts.id) this.dataset["id"] = opts.id;
    this.heading = opts.title || this.heading;
    this.opened = true;
    this.render();
    (this.querySelector("#aia-input") as HTMLTextAreaElement | null)?.focus();
  }

  close(): void {
    this.opened = false;
    this.render();
  }

  private get svc(): any { return (window as any).app.platform.inspectIaReviewService; }

  private async send(preset?: string): Promise<void> {
    const input = this.querySelector("#aia-input") as HTMLTextAreaElement | null;
    const question = (preset ?? input?.value ?? "").trim();
    if (!question || this.busy) return;
    this.messages.push({ role: "user", text: question });
    this.busy = true;
    this.render();
    try {
      const answer = await this.svc.askAssistant(sessionRequestContext(), {
        question, subjectType: (this.dataset["type"] as AIReviewSubjectType) || null, reportId: this.dataset["id"] || null
      });
      this.messages.push({ role: "assistant", text: answer.text, refs: answer.sourceRefs, suggestion: answer.fieldSuggestion });
    } catch (e) {
      this.messages.push({ role: "assistant", text: errorText(e, "Serviço INSPECT IA REVIEW temporariamente indisponível."), error: true });
    }
    this.busy = false;
    this.render();
  }

  private async applySuggestion(index: number): Promise<void> {
    const m = this.messages[index];
    const sug = m?.suggestion;
    const type = this.dataset["type"] as AIReviewSubjectType;
    const id = this.dataset["id"];
    if (!sug || !type || !id) return;
    if (!window.confirm(`Aplicar o texto proposto ao campo "${sug.label}"?\n\nO texto anterior fica guardado e pode ser desfeito. Depois, execute nova revisão IA.`)) return;
    try {
      await this.svc.applyAssistantText(sessionRequestContext(), type, id, sug.fieldPath, sug.baseText, sug.text);
      this.messages[index] = { ...m!, suggestion: null };
      this.messages.push({ role: "assistant", text: `Texto aplicado ao campo "${sug.label}". Execute nova revisão IA antes de enviar para análise.` });
      this.dispatchEvent(new CustomEvent("ai-review-applied", { bubbles: true, detail: { type, id } }));
    } catch (e) {
      this.messages.push({ role: "assistant", text: errorText(e, "Não foi possível aplicar o texto."), error: true });
    }
    this.render();
  }

  private onClick(e: Event): void {
    const el = (e.target as HTMLElement).closest<HTMLElement>("[data-aia]");
    if (!el) return;
    const a = el.dataset["aia"];
    if (a === "close") this.close();
    else if (a === "send") void this.send();
    else if (a === "example") void this.send(el.dataset["q"] || "");
    else if (a === "apply") void this.applySuggestion(Number(el.dataset["i"]));
    else if (a === "clear") { this.messages = []; this.render(); }
  }

  render(): void {
    if (!this.opened) { this.innerHTML = ""; return; }
    const allowed = contextStore.hasPermission(PermissionCode.AiAssistantUse);
    const hasContext = !!(this.dataset["type"] && this.dataset["id"]);
    this.innerHTML = `
      <style>${STYLES}</style>
      <div class="aia-backdrop" data-aia="close"></div>
      <aside class="aia" role="dialog" aria-label="Assistente Técnico INSPECT IA">
        <header class="aia-head">
          <div><div class="aia-kicker">INSPECT IA REVIEW → ASSISTENTE TÉCNICO</div><div class="aia-title">${esc(this.heading)}</div></div>
          <button class="aia-x" data-aia="close" aria-label="Fechar">✕</button>
        </header>
        <p class="aia-note">${hasContext ? "O contexto deste relatório é enviado automaticamente." : "Sem relatório aberto: as respostas usam só a Base Técnica."} Afirmações normativas vêm da Base Técnica, com a fonte. O assistente não escreve no relatório sem sua confirmação.</p>
        ${!allowed ? `<div class="aia-error">Você não possui a permissão ai_assistant.use.</div>` : `
        <div class="aia-log">
          ${this.messages.length === 0 ? `<div class="aia-examples">${EXAMPLES.filter(q => hasContext || !/conclus|recomenda|falta/i.test(q)).map(q => `<button class="aia-chip" data-aia="example" data-q="${esc(q)}">${esc(q)}</button>`).join("")}</div>` : ""}
          ${this.messages.map((m, i) => `
            <div class="aia-msg aia-${m.role}${m.error ? " aia-err" : ""}">
              <div class="aia-text">${esc(m.text).replace(/\n/g, "<br>")}</div>
              ${m.refs && m.refs.length ? `<div class="aia-refs">Fontes: ${m.refs.map(r => `${esc(r.document)}${r.revision ? ` Rev. ${esc(r.revision)}` : ""} — ${esc(r.section)}`).join(" · ")}</div>` : ""}
              ${m.suggestion ? `<div class="aia-sug"><div class="aia-sug-label">Texto proposto para "${esc(m.suggestion.label)}":</div><div class="aia-sug-text">${esc(m.suggestion.text)}</div><button class="aia-btn" data-aia="apply" data-i="${i}">APLICAR AO CAMPO</button></div>` : ""}
            </div>`).join("")}
          ${this.busy ? `<div class="aia-msg aia-assistant"><div class="aia-text">Consultando…</div></div>` : ""}
        </div>
        <footer class="aia-foot">
          <textarea id="aia-input" rows="2" placeholder="Pergunte sobre este relatório ou sobre a Base Técnica…" ${this.busy ? "disabled" : ""}></textarea>
          <div class="aia-foot-actions"><button class="aia-btn aia-ghost" data-aia="clear">LIMPAR</button><button class="aia-btn" data-aia="send" ${this.busy ? "disabled" : ""}>ENVIAR</button></div>
        </footer>`}
      </aside>`;
    const log = this.querySelector(".aia-log");
    if (log) log.scrollTop = log.scrollHeight;
  }
}

const STYLES = `
  .aia-backdrop { position:fixed; inset:0; background: rgba(0,0,0,0.35); z-index:1100; }
  .aia { position:fixed; top:0; right:0; bottom:0; width:min(440px, 100vw); background: var(--ia-bg-panel, #1F2328); color: var(--ia-fg, #E5E7EB); border-left:1px solid var(--ia-line, #3A3F45); z-index:1101; display:flex; flex-direction:column; box-shadow: -12px 0 32px rgba(0,0,0,0.35); }
  .aia-head { display:flex; justify-content:space-between; align-items:flex-start; padding:14px 16px 8px; border-bottom:1px solid var(--ia-line, #3A3F45); }
  .aia-kicker { font-size:0.6rem; letter-spacing:0.06em; color: var(--ia-fg-muted, #9CA3AF); font-weight:700; }
  .aia-title { font-size:0.95rem; font-weight:700; margin-top:2px; }
  .aia-x { background:none; border:none; color:inherit; font-size:1rem; cursor:pointer; }
  .aia-note { font-size:0.7rem; color: var(--ia-fg-muted, #9CA3AF); padding:8px 16px 0; margin:0; line-height:1.45; }
  .aia-log { flex:1; overflow-y:auto; padding:12px 16px; display:flex; flex-direction:column; gap:10px; }
  .aia-examples { display:flex; flex-wrap:wrap; gap:6px; }
  .aia-chip { background: var(--ia-bg-inset, #15181B); color: var(--ia-fg-soft, #CFCFCF); border:1px solid var(--ia-line, #3A3F45); border-radius:14px; padding:5px 10px; font-size:0.72rem; cursor:pointer; text-align:left; }
  .aia-msg { border-radius:8px; padding:8px 10px; font-size:0.8rem; line-height:1.5; max-width:92%; }
  .aia-user { align-self:flex-end; background: var(--ia-action-bg, #3C3C3C); color: var(--ia-action-fg, #fff); }
  .aia-assistant { align-self:flex-start; background: var(--ia-bg-inset, #15181B); border:1px solid var(--ia-line, #3A3F45); }
  .aia-err { border-color: rgba(239,68,68,0.45); color: var(--ia-danger-fg, #fca5a5); }
  .aia-refs { margin-top:6px; font-size:0.66rem; color: var(--ia-fg-muted, #9CA3AF); }
  .aia-sug { margin-top:8px; border-top:1px dashed var(--ia-line, #3A3F45); padding-top:6px; }
  .aia-sug-label { font-size:0.66rem; color: var(--ia-fg-muted, #9CA3AF); }
  .aia-sug-text { margin:4px 0 6px; color: var(--ia-success-fg, #a7f3d0); }
  .aia-foot { border-top:1px solid var(--ia-line, #3A3F45); padding:10px 16px 14px; display:flex; flex-direction:column; gap:6px; }
  .aia-foot textarea { background: var(--ia-bg-inset, #15181B); color: var(--ia-fg, #E5E7EB); border:1px solid var(--ia-line, #3A3F45); border-radius:6px; padding:8px; font-family:inherit; font-size:0.82rem; resize:vertical; }
  .aia-foot-actions { display:flex; justify-content:flex-end; gap:6px; }
  .aia-btn { background: var(--ia-action-bg, #3C3C3C); color: var(--ia-action-fg, #fff); border:none; border-radius:6px; padding:6px 12px; font-size:0.7rem; font-weight:700; cursor:pointer; }
  .aia-btn:disabled { opacity:0.5; }
  .aia-ghost { background: transparent; color: var(--ia-fg-soft, #AEAEAE); border:1px solid var(--ia-line, #3A3F45); }
  .aia-error { margin:12px 16px; border:1px solid rgba(239,68,68,0.45); background: rgba(239,68,68,0.10); color: var(--ia-danger-fg, #fca5a5); border-radius:6px; padding:8px 10px; font-size:0.76rem; }
`;

if (!customElements.get("ai-assistant-drawer")) customElements.define("ai-assistant-drawer", AiAssistantDrawer);
